import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  TablePagination,
} from "@mui/material";

import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  LocalGasStation,
  DirectionsCar,
  Speed,
  UploadFile,
} from "@mui/icons-material";

import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  where,
  limit,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import { db, storage } from "../../firebase";

const PRIMARY = "#0f766e";

const getToday = () => new Date().toISOString().split("T")[0];

const getDefaultForm = () => ({
  vehicleId: "",
  vehicleNumber: "",
  driverId: "",
  driverName: "",
  fuelType: "DIESEL",
  qty: "",
  rate: "",
  amount: "",
  date: getToday(),
  openingReading: "",
  closingReading: "",
  average: "",
  receiptUrl: "",
});

function Fuellogs() {
  const [logs, setLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState("");

  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState(getDefaultForm());
  const [receiptFile, setReceiptFile] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchLogs();
  }, []);

  useEffect(() => {
    if (form.vehicleId && !editId) {
      getLastReading(form.vehicleId);
    }
  }, [form.vehicleId, editId]);

  useEffect(() => {
    const qty = Number(form.qty || 0);
    const rate = Number(form.rate || 0);
    const opening = Number(form.openingReading || 0);
    const closing = Number(form.closingReading || 0);

    const amount = qty * rate;
    const average =
      qty > 0 && closing > opening ? ((closing - opening) / qty).toFixed(2) : "0.00";

    setForm((prev) => ({
      ...prev,
      amount: amount.toFixed(2),
      average,
    }));
  }, [form.qty, form.rate, form.openingReading, form.closingReading]);

  const fetchVehicles = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicles"));

      setVehicles(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.log(err);
      alert("Failed to load vehicles");
    }
  };

  const fetchDrivers = async () => {
    try {
      const q = query(collection(db, "users"), where("role", "==", "Driver"));
      const snap = await getDocs(q);

      setDrivers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.log(err);
      alert("Failed to load drivers");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);

    try {
      const q = query(collection(db, "fuel_logs"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      setLogs(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.log(err);
      alert("Failed to load fuel logs");
    } finally {
      setLoading(false);
    }
  };

  const getLastReading = async (vehicleId) => {
    try {
      const q = query(
        collection(db, "fuel_logs"),
        where("vehicleId", "==", vehicleId),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const snap = await getDocs(q);

      if (!snap.empty) {
        const last = snap.docs[0].data();

        setForm((prev) => ({
          ...prev,
          openingReading: last.closingReading || "",
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const uploadReceipt = async () => {
    if (!receiptFile) return form.receiptUrl || "";

    const fileName = `${Date.now()}_${receiptFile.name}`;
    const storageRef = ref(storage, `fuel_receipts/${fileName}`);

    await uploadBytes(storageRef, receiptFile);
    return await getDownloadURL(storageRef);
  };

  const handleSave = async () => {
    if (!form.vehicleId || !form.driverId || !form.qty || !form.rate || !form.closingReading) {
      alert("Please fill required fields");
      return;
    }

    setSaving(true);

    try {
      const receiptUrl = await uploadReceipt();

      const payload = {
        ...form,
        qty: Number(form.qty),
        rate: Number(form.rate),
        amount: Number(form.amount),
        average: Number(form.average),
        receiptUrl,
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "fuel_logs", editId), payload);
        alert("Fuel log updated successfully");
      } else {
        await addDoc(collection(db, "fuel_logs"), {
          ...payload,
          createdAt: Timestamp.now(),
        });
        alert("Fuel log added successfully");
      }

      setOpen(false);
      resetForm();
      fetchLogs();
    } catch (err) {
      console.log(err);
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Delete fuel log?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "fuel_logs", id));
      alert("Deleted successfully");
      fetchLogs();
    } catch (err) {
      console.log(err);
      alert("Delete failed");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setReceiptFile(null);

    setForm({
      vehicleId: row.vehicleId || "",
      vehicleNumber: row.vehicleNumber || "",
      driverId: row.driverId || "",
      driverName: row.driverName || "",
      fuelType: row.fuelType || "DIESEL",
      qty: row.qty || "",
      rate: row.rate || "",
      amount: row.amount || "",
      date: row.date || getToday(),
      openingReading: row.openingReading || "",
      closingReading: row.closingReading || "",
      average: row.average || "",
      receiptUrl: row.receiptUrl || row.photo || "",
    });

    setOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setReceiptFile(null);
    setForm(getDefaultForm());
  };

  const filteredLogs = useMemo(() => {
    const value = search.toLowerCase();

    return logs.filter((item) => {
      const vehicle = item?.vehicleNumber?.toLowerCase() || "";
      const driver = item?.driverName?.toLowerCase() || "";
      const date = item?.date?.toLowerCase() || "";
      const fuelType = item?.fuelType?.toLowerCase() || "";

      return (
        vehicle.includes(value) ||
        driver.includes(value) ||
        date.includes(value) ||
        fuelType.includes(value)
      );
    });
  }, [logs, search]);

  const paginatedLogs = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredLogs.slice(start, start + rowsPerPage);
  }, [filteredLogs, page, rowsPerPage]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  return (
    <Box p={1} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${PRIMARY}, #134e4a)`,
          color: "#fff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalGasStation fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold">
              Fuel Logs
            </Typography>
          </Stack>

          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
            sx={{ bgcolor: "#fff", color: PRIMARY, fontWeight: "bold" }}
          >
            Add Fuel
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 1, mb: 1, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Fuel Log List
          </Typography>

          <TextField
            size="small"
            placeholder="Search vehicle, driver, date"
            value={search}
            onChange={handleSearch}
            sx={{ width: { xs: "100%", sm: 280 } }}
          />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box p={3} textAlign="center">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                  <TableRow>
                    <TableCell>Sr No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Meter</TableCell>
                    <TableCell>Fuel</TableCell>
                    <TableCell>Average</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell align="center">Receipt</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedLogs.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.date}</TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DirectionsCar fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>
                            {row.vehicleNumber}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip label={row.driverName} color="primary" size="small" />
                      </TableCell>

                      <TableCell>
                        {row.openingReading} - {row.closingReading}
                      </TableCell>

                      <TableCell>
                        {row.fuelType} | {row.qty} L x Rs.{row.rate}
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<Speed />}
                          label={`${row.average} km/L`}
                          color="secondary"
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          Rs.{row.amount}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {row.receiptUrl || row.photo ? (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => {
                              setPreviewImg(row.receiptUrl || row.photo);
                              setPreviewOpen(true);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No file
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>

                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!paginatedLogs.length && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No fuel logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ p: 1.5, fontWeight: "bold", fontSize: 16 }}>
          {editId ? "Update Fuel Log" : "Add Fuel Log"}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 1.5 }}>
          <Grid className="row" spacing={1.5}>
            <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Vehicle"
                value={form.vehicleId}
                onChange={(e) => {
                  const vehicle = vehicles.find((v) => v.id === e.target.value);

                  setForm((prev) => ({
                    ...prev,
                    vehicleId: vehicle?.id || "",
                    vehicleNumber: vehicle?.vehicleNumber || vehicle?.vehicle_no || "",
                  }));
                }}
              >
                {vehicles.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.vehicleNumber || v.vehicle_no}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Driver"
                value={form.driverId}
                onChange={(e) => {
                  const driver = drivers.find((d) => d.id === e.target.value);

                  setForm((prev) => ({
                    ...prev,
                    driverId: driver?.id || "",
                    driverName: driver?.name || driver?.firstname || "",
                  }));
                }}
              >
                {drivers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name || d.firstname}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={form.date}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Fuel Type"
                value={form.fuelType}
                onChange={(e) => setForm((prev) => ({ ...prev, fuelType: e.target.value }))}
              >
                <MenuItem value="DIESEL">DIESEL</MenuItem>
                <MenuItem value="PETROL">PETROL</MenuItem>
                <MenuItem value="CNG">CNG</MenuItem>
                <MenuItem value="EV">EV</MenuItem>
              </TextField>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Opening Meter"
                value={form.openingReading}
                onChange={(e) => setForm((prev) => ({ ...prev, openingReading: e.target.value }))}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Closing Meter"
                value={form.closingReading}
                onChange={(e) => setForm((prev) => ({ ...prev, closingReading: e.target.value }))}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Qty"
                value={form.qty}
                onChange={(e) => setForm((prev) => ({ ...prev, qty: e.target.value }))}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Rate"
                value={form.rate}
                onChange={(e) => setForm((prev) => ({ ...prev, rate: e.target.value }))}
              />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={4}>
              <TextField fullWidth size="small" label="Amount" value={form.amount} disabled />
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <Paper sx={{ p: 1, bgcolor: "#ecfeff", border: "1px solid #a5f3fc" }}>
                <Typography variant="body2" fontWeight="bold">
                  Mileage Average:{" "}
                  <span style={{ color: PRIMARY }}>{form.average} km/L</span>
                </Typography>
              </Paper>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <Button
                fullWidth
                size="small"
                component="label"
                variant="outlined"
                startIcon={<UploadFile />}
              >
                {receiptFile
                  ? receiptFile.name
                  : form.receiptUrl
                  ? "Change Receipt"
                  : "Upload Receipt"}

                <input
                  hidden
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                />
              </Button>
            </Grid>

            <Grid className="col-sm-6 mb-3" item xs={12}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                disabled={saving}
                onClick={handleSave}
                sx={{ bgcolor: PRIMARY, py: 1, fontWeight: "bold" }}
              >
                {saving ? "Saving..." : editId ? "Update Fuel Log" : "Save Fuel Log"}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            p: 1.5,
            fontWeight: "bold",
            fontSize: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Fuel Receipt

          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5 }}>
          {previewImg?.toLowerCase().includes(".pdf") ? (
            <iframe
              src={previewImg}
              title="Fuel Receipt"
              style={{ width: "100%", height: 500, border: 0 }}
            />
          ) : (
            <img
              src={previewImg}
              alt="Fuel Receipt"
              style={{ width: "100%", borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Fuellogs;